import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from "@/Components/ui/select";
import { Button } from "@/Components/ui/button";
import { Item } from "@/types/productTypes";
import { useAdminStore } from "../../../../store/useAdminStore";
import { useState, useEffect } from "react";
import DependencyDialog from "./PriceDependence";
import LearnMoreEditor from "./LearnMoreEditor";
import fetchDb from "@/utils/customRequestFunction";
import { Info } from "lucide-react";
import {
   GenericEditor,
   CustomFieldConfig,
} from "@/Components/GenericEditor/GenericEditor";
import { itemConfig } from "@/configs/editorConfigs";

type ItemEditorProps = {
   item: Item;
};

export default function ItemEditor({ item }: ItemEditorProps) {
   const adminStore = useAdminStore();
   const [learnMoreItems, setLearnMoreItems] = useState<any[]>([]);
   const [isLoading, setIsLoading] = useState(false);

   const {
      selectedProductId,
      selectedStageId,
      selectedOptionId,
      selectedItemId,
   } = adminStore;

   useEffect(() => {
      fetchLearnMoreItems();
   }, []);

   const fetchLearnMoreItems = async () => {
      try {
         setIsLoading(true);
         const items = await fetchDb.get("/api/learn-more");
         setLearnMoreItems(items || []);
      } catch (error) {
         console.error("Error fetching LearnMore items:", error);
      } finally {
         setIsLoading(false);
      }
   };

   const onItemChange = (field: any, value: any) => {
      if (
         !selectedProductId ||
         !selectedStageId ||
         !selectedOptionId ||
         !selectedItemId
      )
         return;
      adminStore.updateItem(
         selectedProductId,
         selectedStageId,
         selectedOptionId,
         selectedItemId,
         field,
         value
      );
   };

   const currentLearnMoreId = item.learnMoreId || null;
   const currentLearnMore = learnMoreItems.find(
      (lm) => lm.id === currentLearnMoreId
   );

   const handleLearnMoreChange = (value: string) => {
      const learnMoreId = value === "none" ? null : parseInt(value);
      onItemChange("learnMoreId", learnMoreId);
   };

   const customConfig: CustomFieldConfig<Item>[] = [
      {
         key: "learnMore",
         label: "Learn More Content",
         type: "custom",
         render: () => (
            <div>
               <div className="flex items-center space-x-2">
                  <Select
                     value={currentLearnMoreId?.toString() || "none"}
                     onValueChange={handleLearnMoreChange}
                     disabled={isLoading}
                  >
                     <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select Learn More content..." />
                     </SelectTrigger>
                     <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {learnMoreItems.map((item) => (
                           <SelectItem key={item.id} value={item.id.toString()}>
                              {item.content.substring(0, 20)}
                              {item.content.length > 20 ? "..." : ""}
                           </SelectItem>
                        ))}
                     </SelectContent>
                  </Select>

                  <Button
                     onClick={() => {
                        fetchLearnMoreItems();
                     }}
                  >
                     <Info className="h-4 w-4" />
                  </Button>

                  <LearnMoreEditor
                     onContentCreated={fetchLearnMoreItems}
                     initialItemToEdit={currentLearnMore}
                  />
               </div>

               {currentLearnMore && (
                  <div className="mt-2 p-3 border rounded-md bg-gray-50">
                     <p className="text-sm text-gray-600">
                        {currentLearnMore.content.substring(0, 20)}
                        {currentLearnMore.content.length > 20 ? "..." : ""}
                     </p>
                     {currentLearnMore.image && (
                        <div className="mt-2">
                           <img
                              src={currentLearnMore.image.url}
                              alt="Learn More"
                              className="h-20 object-contain"
                           />
                        </div>
                     )}
                  </div>
               )}
            </div>
         ),
      },
      {
         key: "dependencies",
         label: "Dependencies",
         type: "custom",
         render: () => <DependencyDialog />,
      },
   ];

   return (
      <div className="flex flex-row w-full h-full">
         <GenericEditor
            item={item}
            config={itemConfig}
            customConfig={customConfig}
            onChange={onItemChange}
            title="Edit Item"
         />
      </div>
   );
}
